import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { TriggerbeeApi } from './../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../core/logger.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { Observable, of } from 'rxjs';
import { Account, BasicAccount } from 'app/back-office/account.model';
import { publishReplay, refCount, share, tap } from 'rxjs/operators';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { InterestProfile } from '../account-settings/models/interest-profiles.model';
import { ConsentModel } from '../account-settings/models/consent.model';
import { EventsModel, GoalModel } from '../account-settings/models/events.model';
import { EventDefinition } from '../automations/automations-models';
import { ApiKeyModel } from '../account-settings/models/api-key.model';
import { Currency } from '../shared/account/Currency';

@Injectable()
export class AccountService {
  private static _accountCache: Observable<Account>;
  private static _siteIdInCache: any;
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    protected readonly router: Router,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('AccountService');
  }

  public async getUsedAccountsAsync(): Promise<any> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/accounts`).toPromise();
  }

  public async updateCurrentAccountSettingsProperty(propertyName: string, value: string): Promise<Account> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const currentSiteId = this.currentSiteIdStore.get();
    const body = [
      {
        op: 'add',
        path: `/${propertyName}`,
        value: value,
      },
    ];
    const accountSettingsResponse = await this.http.patch(`${baseUrl}/accounts/${currentSiteId}`, body);
    return accountSettingsResponse.toPromise();
  }

  public patchCurrentAccountSettingsProperty(properties: { propertyName: string; value: any }[]): Observable<Account> {
    const account = this.patchAccountSettingsProperty(this.currentSiteIdStore.get() ?? 0, properties);
    this.clearAccountCache();
    return account;
  }

  public patchAccountSettingsProperty(
    siteId: number,
    properties: { propertyName: string; value: any }[],
  ): Observable<Account> {
    let body;
    if (properties.length) {
      body = properties.map((p) => ({
        op: 'add',
        path: `/${p.propertyName}`,
        value: p.value,
      }));
    }
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.patch(`${baseUrl}/accounts/${siteId}`, body);
  }

  public getAccount(siteId: number, bypassCache?: boolean): Observable<Account> {
    if (!AccountService._accountCache || AccountService._siteIdInCache !== siteId || bypassCache) {
      AccountService._siteIdInCache = siteId;
      const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
      AccountService._accountCache = this.http.get(`${baseUrl}/accounts/${siteId}`).pipe(
        // Invalidate the cache on error so the next subscriber re-requests
        // instead of receiving the replayed error forever.
        tap({
          error: () => {
            AccountService._accountCache = null;
            AccountService._siteIdInCache = undefined;
          },
        }),
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount(), // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      );
    }
    return AccountService._accountCache;
  }

  public clearAccountCache() {
    AccountService._accountCache = null;
  }

  /** For another account than the current one: getAccount's single-entry cache holds the current account. */
  public getAccountUncached(siteId: number): Observable<Account> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/accounts/${siteId}`);
  }

  public getCurrentAccount(): Observable<Account> {
    const currentSiteId = this.currentSiteIdStore.get();

    if (!currentSiteId) {
      throw new Error('There was no site id in store!');
    }

    return this.getAccount(currentSiteId);
  }

  /**
   * @deprecated Use CurrentContextService and subscribe to BS instead
   */
  public getCurrentAccountAsync(): Promise<Account> {
    return this.getCurrentAccount().toPromise();
  }

  public getAllAccounts(): Observable<BasicAccount[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/accounts`);
  }

  public updateCurrentAccountDetails(accountDetails: Account): Observable<Account> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const persist$ = this.http.put(`${baseUrl}/accounts/${accountDetails.id}`, accountDetails).pipe(share());
    persist$.subscribe(
      (updatedAccount: Account) => {
        sessionStorage.setItem('account-' + updatedAccount.id, JSON.stringify(updatedAccount));
      },
      () => {},
    );

    return persist$;
  }

  public getInterestProfiles(): Observable<Array<InterestProfile>> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseurl}/interest-profiles`);
  }

  public createInterestProfile(topic: InterestProfile): Observable<InterestProfile> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseurl}/interest-profiles`, topic);
  }

  public updateInterestProfile(id: string, topic: InterestProfile): Observable<InterestProfile> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.put(`${baseurl}/interest-profiles/${id}`, topic, id);
  }

  public deleteInterestProfile(id: string): Observable<InterestProfile> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseurl}/interest-profiles/${id}`);
  }

  public getConsent(): Observable<ConsentModel[]> {
    const dataOne: ConsentModel = {
      consentName: 'Email',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: true,
      formConsent: false,
    };
    const dataTwo: ConsentModel = {
      consentName: 'SMS',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: false,
      formConsent: true,
    };
    const datathree: ConsentModel = {
      consentName: 'Postal',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: true,
      formConsent: false,
    };
    const data: ConsentModel[] = [dataOne, dataTwo, datathree];
    const resultObservable = of(data);
    return resultObservable;
  }

  public createConsent(consent: ConsentModel): Observable<ConsentModel[]> {
    const dataOne: ConsentModel = {
      consentName: 'Email',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: true,
      formConsent: false,
    };
    const dataTwo: ConsentModel = {
      consentName: 'SMS',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: false,
      formConsent: true,
    };
    const datathree: ConsentModel = {
      consentName: 'Postal',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: true,
      formConsent: false,
    };
    const data: ConsentModel[] = [dataOne, dataTwo, datathree];
    data.push(consent);
    const resultObservable = of(data);
    return resultObservable;
  }

  public updateConsent(id: string, consent: ConsentModel): Observable<ConsentModel[]> {
    const dataOne: ConsentModel = {
      consentName: 'Email',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: true,
      formConsent: false,
    };
    const dataTwo: ConsentModel = {
      consentName: 'SMS',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: false,
      formConsent: true,
    };
    const datathree: ConsentModel = {
      consentName: 'Postal',
      policy: 'https://site/mypolicy/txt',
      consentText: '<p>Jag samtycker till kommunikation i marknadssyfte och...</p>',
      cookieConsent: true,
      formConsent: false,
    };
    const data: ConsentModel[] = [dataOne, dataTwo, datathree];
    const resultObservable = of(data);
    return resultObservable;
  }

  public deleteConsent(id: string): Observable<ConsentModel> {
    const returnObservable = of(null);
    return returnObservable;
  }

  public getGoals(): Observable<GoalModel[]> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseurl}/goals`);
  }

  public createGoal(goal: GoalModel): Observable<GoalModel> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseurl}/goals`, goal);
  }

  public updateGoal(goal: GoalModel): Observable<GoalModel> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.put(`${baseurl}/goals`, goal);
  }

  public deleteGoal(goal: GoalModel): Observable<GoalModel> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseurl}/goals`, null, goal);
  }

  public deleteMultipleGoals(goalModels: Array<GoalModel>): Observable<GoalModel> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseurl}/goals/multiple`, null, goalModels);
  }

  public getPageViews(): Observable<GoalModel[]> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseurl}/goals/page-views`);
  }

  /** Events the account is set up to trigger on. Written when something starts using one, or added here. */
  public getEventDefinitions(): Observable<EventDefinition[]> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseurl}/event-definitions`);
  }

  /** Null when the account already has the event and scope - the catalogue holds one entry per pair. */
  public createEventDefinition(request: {
    type: string;
    widgetId: number;
    componentId: number;
  }): Observable<EventDefinition> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseurl}/event-definitions`, request);
  }

  public deleteEventDefinition(id: string): Observable<void> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseurl}/event-definitions/${id}`);
  }

  public getEvents(): Observable<EventsModel[]> {
    const dataOne: EventsModel = { name: 'Cancel subscription' };
    const dataTwo: EventsModel = { name: 'Viewed product' };
    const data = [dataOne, dataTwo];
    const returnObservable = of(data);
    return returnObservable;
  }

  public createEvent(event: EventsModel): Observable<EventsModel> {
    const data = null;
    const returnObservable = of(data);
    return returnObservable;
  }

  public updateEvent(event: EventsModel, id: string): Observable<EventsModel> {
    const data = null;
    const returnObservable = of(data);
    return returnObservable;
  }

  public deleteEvent(id: string): Observable<EventsModel> {
    const data = null;
    const returnObservable = of(data);
    return returnObservable;
  }

  public getApiKeys(): Observable<ApiKeyModel[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/accounts/api-keys`);
  }

  public createApiKey(key: ApiKeyModel): Observable<ApiKeyModel> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/accounts/api-keys`, key);
  }

  public deleteApiKey(id: string): Observable<ApiKeyModel> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseUrl}/accounts/api-keys/${id}`);
  }

  public updateAccount(id: string, account: Account): Observable<Account> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.put(`${baseUrl}/accounts/${id}`, account);
  }

  public getUiPreferences(): Observable<string | null> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.getDownload(`${baseUrl}/ui-preferences`);
  }

  public updateUiPreferences(value: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.put(`${baseUrl}/ui-preferences`, value);
  }

  /**
   * Fire-and-forget variant of updateUiPreferences that survives page unload.
   * Uses a keepalive fetch (cookie-authenticated like all gateway requests) so a
   * pending save isn't lost when the user reloads or closes the tab.
   */
  public updateUiPreferencesOnUnload(value: string): void {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    fetch(`${baseUrl}/ui-preferences`, {
      method: 'PUT',
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: value,
    });
  }

  public getDefaultSubscriberWorth(currency: Currency) {
    if (currency) {
      switch (currency) {
        case Currency.Sek:
          return 75;
        case Currency.Eur:
          return 8;
      }
    }
    return null;
  }

  public static cleanupDomains(val): string[] {
    if (!val) {
      return [];
    }

    return val
      .filter((url) => url?.trim().length > 0)
      .map((url) => {
        const isProtocol = url.includes('http://') || url.includes('https://');
        let resValue = isProtocol ? url.slice(url.indexOf('//') + 2) : url;
        const idxPathSlash = resValue.indexOf('/');
        resValue =
          idxPathSlash !== -1 && resValue.slice(idxPathSlash).indexOf('.') === -1
            ? resValue.slice(0, idxPathSlash)
            : resValue;
        return resValue;
      });
  }
}
